import { Link } from "react-router-dom";
import { ArrowRight, Target, Compass, Building2, History, GraduationCap, Users, MessageCircle, Utensils, Heart, CheckCircle2 } from "lucide-react";
import SectionEyebrow from "@/components/SectionEyebrow";
import { IMAGES, VALUES, STRUCTURE, ACHIEVEMENTS } from "@/data/content";

const ACHIEVEMENT_ICONS = [GraduationCap, MessageCircle, Users, Utensils, Heart, Building2, CheckCircle2];

const About = () => {
  return (
    <div data-testid="about-page">
      {/* HEADER */}
      <section className="relative min-h-[55vh] flex items-end overflow-hidden">
        <img src={IMAGES.evangelisation} alt="MIREVSA équipe" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B3D91]/90 via-[#0B3D91]/60 to-[#0B3D91]/30" />
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative mv-container py-24 text-white">
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-white/80" />
            <span className="text-xs uppercase tracking-[0.3em] font-bold">Notre histoire</span>
          </div>
          <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95] max-w-4xl">
            Qui sommes-nous.
          </h1>
          <p className="mt-6 text-lg text-blue-50/95 max-w-2xl">
            Découvrez l'histoire, la vision et les valeurs qui animent MIREVSA depuis sa fondation.
          </p>
        </div>
      </section>

      {/* VISION */}
      <section className="mv-section bg-white" data-testid="vision-section">
        <div className="mv-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden rounded-2xl">
              <img
                src="https://customer-assets-0z36b82j.emergentagent.net/job_kingdom-harvest-1/artifacts/te9adgw8_Voir%20des%20vies%20transform%C3%A9es.jpeg"
                alt="Voir des vies transformées"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className="lg:col-span-7 lg:pl-4">
            <SectionEyebrow>Vision</SectionEyebrow>
            <h2 className="mt-5 font-heading font-black text-4xl sm:text-5xl tracking-tight text-[#1C1C1C] leading-[1.05]">
              Voir des vies transformées.
            </h2>
            <p className="mt-6 text-lg text-muted leading-relaxed">
              La vision est de voir des vies profondément transformées par la puissance de l'Évangile,
              entraînant la transformation des nations à travers des communautés solides,
              des leaders bien formés et des personnes restaurées dans tous les domaines de leur vie.
            </p>
            <div className="mt-8 flex items-start gap-3">
              <Compass size={22} className="text-[#0B3D91] mt-1 shrink-0" />
              <p className="text-base text-muted">
                Une vision globale, spirituelle, humaine et communautaire.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="mv-section bg-[#F8FAFC]" data-testid="mission-section">
        <div className="mv-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 lg:order-1 order-2">
            <SectionEyebrow>Mission</SectionEyebrow>
            <h2 className="mt-5 font-heading font-black text-4xl sm:text-5xl tracking-tight text-[#1C1C1C] leading-[1.05]">
              L'évangélisation complète.
            </h2>
            <p className="mt-6 text-lg text-muted leading-relaxed">
              La mission de MIREVSA est de promouvoir une évangélisation complète, qui combine
              l'annonce de l'Évangile avec la transformation globale des vies et des communautés.
              Elle intervient à travers la propagation de l'Évangile, la prière, la formation et
              les actions sociales, afin de restaurer la dignité humaine, renforcer les familles,
              accompagner la jeunesse et répondre aux besoins essentiels des populations.
            </p>
            <p className="mt-4 text-lg text-muted leading-relaxed">
              Cette mission s'étend également au développement humain et communautaire, en favorisant
              l'éducation, la solidarité, l'autonomisation et l'inclusion sociale.
            </p>
            <div className="mt-8 flex items-start gap-3">
              <Target size={22} className="text-[#0B3D91] mt-1 shrink-0" />
              <p className="text-base text-muted">
                Une transformation durable, dans toutes les dimensions de la vie.
              </p>
            </div>
          </div>
          <div className="lg:col-span-5 lg:order-2 order-1">
            <div className="aspect-[4/5] overflow-hidden rounded-2xl">
              <img
                src="https://customer-assets-0z36b82j.emergentagent.net/job_kingdom-harvest-1/artifacts/9wov6n9q_L%27%C3%A9vang%C3%A9lisation%20compl%C3%A8te.jpeg"
                alt="L'évangélisation complète"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* STRUCTURE */}
      <section className="mv-section bg-white" data-testid="structure-section">
        <div className="mv-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-14">
            <div className="lg:col-span-6">
              <SectionEyebrow>Structure</SectionEyebrow>
              <h2 className="mt-5 font-heading font-black text-4xl sm:text-5xl tracking-tight text-[#1C1C1C] leading-[1.05]">
                Organisation structurée.
              </h2>
              <p className="mt-5 text-lg text-muted leading-relaxed">
                MIREVSA s'organise autour de quatre départements et d'une Direction de la Communication
                et du Marketing, formant une structure complémentaire au service de sa mission d'évangélisation complète.
              </p>
            </div>
            <div className="lg:col-span-6">
              <div className="aspect-[16/10] overflow-hidden rounded-2xl">
                <img
                  src="https://customer-assets-0z36b82j.emergentagent.net/job_kingdom-harvest-1/artifacts/gjpd6tsu_Organisation%20structur%C3%A9e.jpeg"
                  alt="Organisation structurée"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* 5 cards: 3 on top row, 2 centered on bottom row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-5">
            {STRUCTURE.map((s, i) => (
              <div
                key={s.name}
                className={`bg-white p-8 border border-gray-200 hover:border-[#0B3D91]/40 hover:shadow-lg transition-all duration-300 lg:col-span-2 ${
                  i === 3 ? "lg:col-start-2" : ""
                }`}
                data-testid={`structure-${i}`}
              >
                <div className="h-10 w-10 bg-[#0B3D91] text-white flex items-center justify-center font-heading font-black">
                  {i + 1}
                </div>
                <h3 className="mt-5 font-heading font-bold text-lg text-[#1C1C1C] leading-tight">{s.name}</h3>
                <p className="mt-2 text-sm text-muted leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HISTORIQUE */}
      <section className="mv-section bg-[#F8FAFC]" data-testid="historique-section">
        <div className="mv-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-5">
              <SectionEyebrow>Historique</SectionEyebrow>
              <h2 className="mt-5 font-heading font-black text-4xl sm:text-5xl tracking-tight text-[#1C1C1C] leading-[1.05]">
                Nos parcours.
              </h2>
              <div className="mt-8 flex items-center gap-4 border-l-4 border-[#0B3D91] pl-5">
                <History size={28} className="text-[#0B3D91]" />
                <div>
                  <div className="font-heading font-black text-3xl text-[#1C1C1C]">2011</div>
                  <div className="text-sm text-muted uppercase tracking-widest">Début des activités</div>
                </div>
              </div>
              <div className="mt-4 flex items-center gap-4 border-l-4 border-[#0B3D91] pl-5">
                <Building2 size={28} className="text-[#0B3D91]" />
                <div>
                  <div className="font-heading font-black text-3xl text-[#1C1C1C]">13 sept. 2024</div>
                  <div className="text-sm text-muted uppercase tracking-widest">Enregistrement officiel MIREVSA</div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-7 text-base text-muted leading-relaxed space-y-4">
              <p>
                Les activités de MIREVSA — notamment dans l'évangélisation, la formation et la prière —
                ont commencé en <span className="font-bold text-[#1C1C1C]">2011</span> sous les appellations
                <span className="font-semibold text-[#1C1C1C]"> REP/MEPSA</span> (évangélisation et formation)
                et <span className="font-semibold text-[#1C1C1C]"> PIM</span> (intercession).
              </p>
              <p>
                Pendant plusieurs années sur le terrain, nous avons accompagné <span className="font-bold text-[#1C1C1C]">plus de quarante églises</span>,
                principalement dans la zone de Bas-Delmas, à travers des actions de formation, d'encadrement
                et d'orientation en évangélisation, avec le soutien d'une <span className="font-semibold text-[#1C1C1C]">quinzaine de conférenciers</span>.
              </p>
              <p>
                Des programmes d'études bibliques ont été mis en place dans plusieurs établissements
                scolaires — notamment <span className="font-semibold text-[#1C1C1C]">COJEMA</span> et
                <span className="font-semibold text-[#1C1C1C]"> AJHENA</span> — afin d'annoncer la Bonne Nouvelle
                et d'accompagner les jeunes dans leur développement spirituel et moral.
              </p>
              <p>
                L'instabilité politique en Haïti a conduit l'organisation à une phase de réorganisation stratégique.
                Cette réflexion a fait émerger une vision plus globale : un programme d'évangélisation complète,
                intégrant à la fois les dimensions spirituelles, humaines et sociales — ce qui a mené à l'enregistrement
                officiel sous le nom de <span className="font-bold text-[#1C1C1C]">MIREVSA le 13 septembre 2024</span>.
              </p>
            </div>
          </div>

          {/* NOS PARCOURS — Featured image */}
          <div className="mb-12 rounded-2xl overflow-hidden">
            <img
              src="https://customer-assets-0z36b82j.emergentagent.net/job_kingdom-harvest-1/artifacts/3ykuu1qb_Nos%20parcours.jpeg"
              alt="Nos parcours — MIREVSA sur le terrain"
              loading="lazy"
              className="w-full h-[420px] md:h-[520px] object-cover object-center"
            />
          </div>

          {/* ACHIEVEMENTS */}
          <div className="bg-white border border-gray-200 p-8 lg:p-12">
            <div className="max-w-2xl">
              <SectionEyebrow>Réalisations</SectionEyebrow>
              <h3 className="mt-4 font-heading font-black text-3xl tracking-tight text-[#1C1C1C]">
                Principales réalisations depuis la restructuration.
              </h3>
            </div>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-5">
              {ACHIEVEMENTS.map((a, i) => {
                const Icon = ACHIEVEMENT_ICONS[i % ACHIEVEMENT_ICONS.length];
                return (
                  <div key={i} className="flex gap-4" data-testid={`achievement-${i}`}>
                    <div className="h-9 w-9 bg-[#0B3D91]/10 flex items-center justify-center shrink-0">
                      <Icon size={18} className="text-[#0B3D91]" />
                    </div>
                    <p className="text-sm text-muted leading-relaxed pt-1.5">{a}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* CONCLUSION */}
          <div className="mt-12 max-w-3xl">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#0B3D91]">Conclusion</div>
            <p className="mt-4 text-lg text-muted leading-relaxed">
              Aujourd'hui, MIREVSA poursuit sa mission avec la conviction que l'Évangile doit produire
              un impact concret, durable et global dans la société. Nous croyons que chaque soutien peut
              contribuer à transformer des vies et des communautés.
            </p>
          </div>
        </div>
      </section>

      {/* VALEURS */}
      <section className="mv-section bg-white" data-testid="values-section">
        <div className="mv-container">
          <div className="max-w-2xl mb-14">
            <SectionEyebrow>Nos valeurs</SectionEyebrow>
            <h2 className="mt-5 font-heading font-black text-4xl sm:text-5xl tracking-tight text-[#1C1C1C]">
              Nos valeurs fondamentales.
            </h2>
            <p className="mt-5 text-base text-muted leading-relaxed">
              Ces valeurs guident chacune de nos actions et décisions.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-gray-200 border border-gray-200">
            {VALUES.map((v, i) => (
              <div key={v.title} className="bg-white p-8 hover:bg-[#F8FAFC] transition-colors" data-testid={`value-${i}`}>
                <div className="font-heading font-black text-5xl text-[#0B3D91]">0{i + 1}</div>
                <div className="mt-5 font-heading font-bold text-xl text-[#1C1C1C]">{v.title}</div>
                <p className="mt-2 text-sm text-muted leading-relaxed">{v.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0B3D91] text-white py-24" data-testid="about-cta">
        <div className="mv-container text-center">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="h-px w-10 bg-white/80" />
            <span className="text-xs uppercase tracking-[0.3em] font-bold">Rejoignez-nous</span>
            <span className="h-px w-10 bg-white/80" />
          </div>
          <h2 className="font-heading font-black text-4xl sm:text-5xl tracking-tight max-w-2xl mx-auto">
            Faites partie de l'histoire.
          </h2>
          <p className="mt-6 text-lg text-blue-100 max-w-2xl mx-auto">
            Rejoignez MIREVSA et participez à une mission qui change des vies. Ensemble, nous pouvons accomplir bien plus.
          </p>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/join" className="bg-white text-[#0B3D91] hover:bg-blue-50 inline-flex items-center justify-center gap-2 px-7 py-3.5 font-bold transition-all">
              Rejoindre la mission <ArrowRight size={18} />
            </Link>
            <Link to="/contact" className="border-2 border-white text-white hover:bg-white hover:text-[#0B3D91] inline-flex items-center justify-center gap-2 px-7 py-3.5 font-bold transition-all">
              Nous contacter
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
